/* eslint-disable no-console */
import { throwError, EMPTY, Observable, switchMap, of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { HttpErrorResponse, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Injectable, inject, isDevMode } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { API_OPTIONS, ApiOptions } from '@ng-gems/api';

import { AlertService } from '@app/core/services/alert.service';
import { AccessControlScope } from '@models/account/access-control-scope.model';

import { HeaderInjectorService } from './header-injector.service';

interface EndpointError {
  message: string;
  details: string;
}

@Injectable()
export class ErrorCatcherService extends HeaderInjectorService {
  private alertService = inject(AlertService);
  private translationService = inject(TranslocoService);
  private started: number;
  private errorDetected: boolean;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const options: ApiOptions = req.context.get(API_OPTIONS);
    if (!options.backendId) {
      return next.handle(req);
    }
    this.started = Date.now();
    const headers = ApiOptions.cleanHeaders(req.headers);
    return next.handle(req.clone({ headers })).pipe(
      tap(() => (this.errorDetected = false)),
      catchError((err: HttpErrorResponse) => {
        this.errorDetected = true;
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            // Whatever the channel is, we handle the 401 (unauthorized) error and 303
            case 401: {
              if (!options.reloadIgnore) {
                // If 401 occurs on this endpoint, no need to refresh token as it would trigger another call on this endpoint
                // that would generate another 401... and we would end up with an infinite loop..
                if (this.oktaService.hasValidAccessToken()) {
                  return super.intercept(req, next);
                }
              }
              this.alertService.error('alert.logout');
              setTimeout(() => this.authService.logout(), 2000);
              return throwError(() => err);
            }
            case 303: {
              if (options.interceptRedirection) {
                this.alertService.warning('alert.download_pending');
                return EMPTY;
              }
            }
          }
        }
        if (options.interceptErrors) {
          return this.notifyError(err, options);
        }
        return throwError(() => err);
      }),
      finalize(() => this.checkCapacities(req, options)),
    );
  }

  private checkCapacities(req: HttpRequest<any>, options: ApiOptions) {
    if (!isDevMode() || !this.authService.user || this.authService.user.hasAllCapacities(options.capacities, new AccessControlScope())) {
      return;
    }
    let message = 'Endpoint needs some capacities, but current user is not granted with those. Needs fixing on frontend';
    if (!this.errorDetected) {
      message += 'Further more, this query has been accepted by backend, but it should have not. Needs fixing on backend';
    }
    this.alertService.error(message, false);
    console.group('Capacities not respected');
    console.warn('The endpoint', req.url, 'needs following capacities', options.capacities);
    console.warn('But current logged user is not granted with those. User grants are', this.authService.user.accessControlGrants);
    if (!this.errorDetected) {
      console.warn('Further more, backend has not refused this call from frontend => there is therefore a security hole on backend side');
    }
    console.groupEnd();
  }

  private parseBackendError(err: HttpErrorResponse): EndpointError {
    let extra = '';
    if (err.error.fields && Array.isArray(err.error.fields)) {
      extra = '(' + err.error.fields.join(', ') + ')';
    }

    const details = typeof err.error.details === 'string' ? err.error.details : undefined;
    const bag = [];
    if (err.error.message) {
      bag.push(err.error.message);
    }
    if (details) {
      bag.push(details);
    }
    if (extra) {
      bag.push(extra);
    }

    return { message: details || err.error.message || err.error.errorMessage, details: bag.length ? bag.join(' ') : undefined };
  }

  private getFallbackError(err: HttpErrorResponse, options: ApiOptions): EndpointError {
    const elapsed = Date.now() - this.started;
    const message = `alert.channels.${options.channel}`;

    let details = 'alert.network_error';
    let params: unknown = {
      message: '',
    };

    if (err instanceof HttpErrorResponse) {
      details = 'alert.backend_error';
      params = {
        elapsed,
        status: err.status,
        statusText: err.statusText,
        url: err.url,
        // If we want to be able to retrieve the Request Id, server must set this header:
        // Access-Control-Expose-Headers: X-Request-Id
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Access-Control-Expose-Headers
        requestId: err.headers.get('X-Request-Id'),
      };
    }

    return {
      message:
        this.translationService.translate(message, params) || err.status === 422
          ? this.translationService.translate('alert.channels.dataInconsistency')
          : this.translationService.translate('alert.channels.general'),
      details: this.translationService.translate(details, params),
    };
  }

  private notifyError(err: HttpErrorResponse, options: ApiOptions) {
    // For an endpoint that downloads Blob, the error is in Blob
    // So we need first to convert into valid json error to be parseable
    return this.blob2String(err.error).pipe(
      tap((errorString: string) => {
        let error: string | unknown = errorString;

        if (err.headers.get('Content-Type')?.split(';')?.includes('application/json')) {
          try {
            error = JSON.parse(errorString);
          } catch (_e) {
            /* empty */
          }
        } else {
          error = {};
        }
        err = { ...err, error };

        let infos = this.parseBackendError(err);
        if (!infos.message) {
          infos = this.getFallbackError(err, options);
        }

        this.alertService.error(infos.message, false);

        // We give more information on non-production in the console
        if (!isDevMode()) {
          return;
        }
        console.group(infos.message);
        if (infos.details) {
          console.error(infos.details);
        }
        if (err.error) {
          console.error(err.error);
        }
        console.groupEnd();
        /* eslint-enable no-console */
      }),
      switchMap(() => EMPTY),
    );
  }

  private blob2String(message: string | Blob): Observable<string | ArrayBuffer> {
    if (message instanceof Blob) {
      return new Observable((observer) => {
        const reader = new FileReader();
        reader.readAsText(message);
        reader.addEventListener('loadend', () => {
          observer.next(reader.result);
          observer.complete();
        });
      });
    } else {
      return of(message);
    }
  }
}
